/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import MediaProcessErrorType from 'linced-app/enums/media-process-error-type';
import MediaProcessType from 'linced-app/enums/media-process-type';
import MediaStatus from 'linced-app/enums/media-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';

export default class ReplacementMediaController extends Controller {
  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  // Attrs
  Scopes = Scopes;

  // fileTypes specifies the file types allowed to be uploaded.
  fileTypes = Object.freeze([
    'application/zip',
    'application/x-zip-compressed',
    '.zip',
  ]);

  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  // State
  // currentStep provides the state of which step the stepper is at.
  @tracked
  currentStep = 0;

  // uploading provides the state of uploading.
  @tracked
  uploading = false;

  // uploadStatus holds the returned response from the server
  @tracked uploadStatus = null;

  @tracked
  pollTimer;

  // Methods
  // countProcessed calculates the number of replacements processed.
  @computed('uploadStatus.errors')
  get countProcessed() {
    let processed = this.get('uploadStatus.processed');
    return processed || 0;
  }

  @computed('uploadStatus.errors.[]')
  get processErrors() {
    return this.get('uploadStatus.errors') ?? [];
  }

  // countErrors calculates the number of replacements that failed.
  @computed('processErrors.@each.error')
  get countErrors() {
    let errors = this.processErrors.filter(
      (item) => item.error === MediaProcessErrorType.ERROR,
    );
    return errors.length;
  }

  // countSkipped calculates the number of replacements that were skipped.
  @computed('processErrors.@each.error')
  get countSkipped() {
    let skipped = this.processErrors.filter(
      (item) => item.error === MediaProcessErrorType.SKIPPED,
    );
    return skipped.length;
  }

  // countSuccess calculates the number of photos that succeeded.
  @computed('countErrors', 'countSkipped', 'uploadStatus.processed')
  get countSuccess() {
    let count = this.get('uploadStatus.processed');
    let success = count - this.countErrors - this.countSkipped;
    if (success < 0) {
      return 0;
    }
    return success || 0;
  }

  // processing calculates whether the bulk replacement process is in progress.
  @computed('uploadStatus', 'uploadStatus.status')
  get processing() {
    if (isEmpty(this.uploadStatus)) {
      return false;
    }
    return this.get('uploadStatus.status') === MediaStatus.PROCESSING;
  }

  updateStatus() {
    if (isEmpty(this.uploadStatus)) {
      return;
    }
    this.uploadStatus.reload().then((status) => {
      switch (status.status) {
        case MediaStatus.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          break;
      }
    });
  }

  // onUploadStart provides the action callback for when uppy starts pushing
  // the file to the server.
  @action
  onUploadStart() {
    this.uploading = true;
  }

  @action
  onError() {
    this.paperToaster.show('Error uploading replacement media!', {
      duration: 4000,
      position: 'top right',
      toastClass: 'warning',
    });
  }

  // uploaded provides the upload complete callback handler.
  @action
  onUpload(file, upload) {
    let key = upload.uploadURL.match(/files\/(.+)\+/)[1];
    let person = this.get('session.person');

    if (!isEmpty(this.pollTimer)) {
      // Cancel any pending status poll.
      cancel(this.pollTimer);
    }

    let newStatusRecord = {
      schoolId: this.get('session.school'),
      requesterId: this.get('session.person'),
      jurisdiction: this.get('session.school.jurisdiction'),
      process: MediaProcessType.BULK_REPLACE,
      storageKey: key,
    };

    this.store
      .createRecord('media/status', newStatusRecord)
      .save()
      .then((mediaStatus) => {
        this.paperToaster.show(`Upload succeeded!`, {
          duration: 8000,
          position: 'top right',
          toastClass: 'success',
        });

        let pollTimer = debounce(this, 'updateStatus', 2000, false);
        this.uploadStatus = mediaStatus;
        this.currentStep = this.currentStep + 1;
        this.pollTimer = pollTimer;
      })
      .catch(() => {
        person.rollbackAttributes(); // Revert unsaved changes, i.e. the profileMediaId
        this.paperToaster.show('Error processing replacement media!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .finally(() => {
        this.uploading = false;
      });
  }

  // reset resets the page back to defaults.
  reset() {
    this.currentStep = 0;
    this.uploading = false;
  }
}
